import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getSubjectById } from '../../config/subjects';
import { useAuth } from '../Auth/AuthContext';
import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function StudyGuidesView() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const subject = getSubjectById(subjectId);

  const [guides, setGuides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGuide, setSelectedGuide] = useState(null);

  useEffect(() => {
    if (!subject) {
      setError('Subject not found');
      setIsLoading(false);
      return;
    }

    fetchStudyGuides();
  }, [subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStudyGuides = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await axios.get(`${API_BASE_URL}/api/study-guides`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        params: {
          subjectId: subject.wixId // Use Wix ID for filtering
        }
      });

      setGuides(response.data.guides || []);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch study guides:', err);
      setError('Failed to load study guides');
      setIsLoading(false);
    }
  };

  const handleGuideClick = async (guide) => {
    if (!isSubscribed) {
      navigate('/pricing');
      return;
    }

    // Get pre-signed URL for PDF
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await axios.get(
        `${API_BASE_URL}/api/study-guides/${guide.guideId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Open PDF in modal or new tab
      setSelectedGuide({
        ...guide,
        pdfUrl: response.data.url
      });
    } catch (err) {
      console.error('Failed to get PDF URL:', err);
      setError('Failed to load PDF');
    }
  };

  const closeGuideModal = () => {
    setSelectedGuide(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-study-teal mx-auto mb-4"></div>
          <p className="text-study-black text-xl">Loading study guides...</p>
        </div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-study-black mb-4">Subject Not Found</h1>
          <Link
            to="/academy"
            className="bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-yellow transition-all"
          >
            Back to Essentials
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/academy/${subjectId}`)}
            className="flex items-center text-study-black hover:text-white transition-all mb-4"
          >
            <span className="text-2xl mr-2">←</span>
            <span className="font-bold">Back to {subject.name}</span>
          </button>
          <div className="flex items-center gap-4">
            <span className="text-5xl">{subject.icon}</span>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-study-black">
                {subject.name} Study Guides
              </h1>
              <p className="text-study-black text-lg">
                {guides.length} comprehensive study guides available
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="bg-red-200 p-4 rounded-lg text-center">
            <p className="text-study-black text-lg font-bold">{error}</p>
          </div>
        </div>
      )}

      {/* Premium Warning for Free Users */}
      {!isSubscribed && guides.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="bg-study-yellow p-6 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-study-black mb-2">
              🔒 Premium Content
            </h3>
            <p className="text-study-black mb-4">
              Study guides are available exclusively for Premium members
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-pink transition-all"
            >
              Upgrade to Premium
            </Link>
          </div>
        </div>
      )}

      {/* Study Guides Grid */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        {guides.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-study-black text-xl mb-4">
              No study guides available yet for {subject.name}
            </p>
            <p className="text-gray-600">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {guides.map((guide) => (
              <GuideCard
                key={guide.guideId}
                guide={guide}
                isSubscribed={isSubscribed}
                onClick={() => handleGuideClick(guide)}
              />
            ))}
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {selectedGuide && (
        <PDFModal guide={selectedGuide} onClose={closeGuideModal} />
      )}
    </div>
  );
}

function GuideCard({ guide, isSubscribed, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden group"
    >
      {/* Thumbnail */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        {guide.thumbnailUrl ? (
          <img
            src={guide.thumbnailUrl}
            alt={guide.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl">📄</span>
          </div>
        )}
        {/* Premium Badge Overlay */}
        {!isSubscribed && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-study-yellow text-study-black px-4 py-2 rounded-lg font-bold">
              🔒 Premium
            </div>
          </div>
        )}
      </div>

      {/* Guide Info */}
      <div className="p-4">
        <h3 className="text-study-black font-bold text-lg mb-2 line-clamp-2">
          {guide.title}
        </h3>
        <button className="w-full bg-study-teal text-study-black font-semibold py-2 rounded-lg hover:bg-study-pink transition-all">
          {isSubscribed ? 'View Guide' : 'Unlock'}
        </button>
      </div>
    </div>
  );
}

function PDFModal({ guide, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-2xl font-bold text-study-black">{guide.title}</h2>
          <button
            onClick={onClose}
            className="text-study-black hover:text-study-pink text-3xl font-bold"
          >
            ✕
          </button>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden">
          <iframe
            src={guide.pdfUrl}
            className="w-full h-full"
            title={guide.title}
          />
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t flex justify-between items-center">
          <a
            href={guide.pdfUrl}
            download={`${guide.title}.pdf`}
            className="bg-study-teal text-study-black font-bold px-6 py-2 rounded-lg hover:bg-study-yellow transition-all"
          >
            Download PDF
          </a>
          <button
            onClick={onClose}
            className="text-study-black hover:text-study-pink font-bold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudyGuidesView;
